import { AppCtx } from "@/wab/client/app-ctx";
import {
  ImageAssetOpts,
  maybeUploadImage,
  readAndSanitizeSvgXmlAsImage,
  ResizableImage,
} from "@/wab/client/dom-utils";
import { parseHtmlToWebImporterTree } from "@/wab/client/web-importer/html-parser";
import {
  isWIBaseVariantSettings,
  WIAnimationSequence,
  WIBase,
  WIElement,
  WIFragment,
  WIScreenVariant,
  WIStyleVariant,
  WIVariant,
} from "@/wab/client/web-importer/types";
import { ProjectId } from "@/wab/shared/ApiSchema";
import { CodeComponentsRegistry } from "@/wab/shared/code-components/code-components";
import { paramToVarName, toVarName } from "@/wab/shared/codegen/util";
import { assertNever, mkShortId, withoutNils } from "@/wab/shared/common";
import {
  code,
  codeLit,
  customCode,
  InteractionConditionalMode,
} from "@/wab/shared/core/exprs";
import { ImageAssetType } from "@/wab/shared/core/image-asset-type";
import { getTagAttrForImageAsset } from "@/wab/shared/core/image-assets";
import { JsonValue, mkNameArg } from "@/wab/shared/core/lang";
import {
  allAnimationSequences,
  getResponsiveStrategy,
} from "@/wab/shared/core/sites";
import { validateStylesForTpl } from "@/wab/shared/core/style-props-tpl";
import {
  mkRuleSet,
  tryGetAnimationSequenceUuidFromCssVar,
} from "@/wab/shared/core/styles";
import { AttrsSpec, TplTagType } from "@/wab/shared/core/tpls";
import { camelCssPropsToKebab } from "@/wab/shared/css";
import {
  AnimationProperty,
  CssAnimation,
  isAnimationProperty,
  parseCssAnimationsFromStyles,
} from "@/wab/shared/css/animations";
import {
  Animation,
  AnimationSequence,
  Component,
  CustomCode,
  EventHandler,
  FunctionExpr,
  ImageAssetRef,
  Interaction,
  isKnownDateRangeStrings,
  isKnownDateString,
  isKnownTplTag,
  KeyFrame,
  Param,
  RawText,
  Site,
  TplNode,
  TplTag,
  VariantsRef,
} from "@/wab/shared/model/classes";
import {
  isAnyType,
  isChoiceType,
  isMultiChoiceType,
  wabToTsType,
} from "@/wab/shared/model/model-util";
import { ResponsiveStrategy } from "@/wab/shared/responsiveness";
import { RSH } from "@/wab/shared/RuleSetHelpers";
import { isSlot } from "@/wab/shared/SlotUtils";
import { TplMgr } from "@/wab/shared/TplMgr";
import {
  ensureVariantSetting,
  getBaseVariant,
  getOrderedScreenVariantSpecs,
  getPrivateStyleVariantsForTag,
  isStandaloneVariantGroup,
  VariantCombo,
  VariantGroupType,
} from "@/wab/shared/Variants";
import { VariantTplMgr } from "@/wab/shared/VariantTplMgr";
import { deserializePlasmicComponentAttrs } from "@/wab/shared/web-exporter/component-utils";
import L, { isArray } from "lodash";

export interface HtmlToTplResult {
  /** Tpl nodes ready to be inserted (multiple when root is a WIFragment) */
  tpls: TplNode[];
  /**
   * Finalize deferred changes that must happen inside studioCtx.change():
   * animation sequences, variant styles, and image asset attachment.
   */
  finalize: (opts: {
    component: Component;
    tplMgr: TplMgr;
    ccRegistry: CodeComponentsRegistry;
  }) => void;
}

/**
 * Convert an HTML string into a TplNode tree with all styles applied.
 *
 * @param html - The HTML string to convert.
 * @param opts - Site, VariantTplMgr, and AppCtx needed for conversion.
 * @returns `{ tpls, finalize }` where:
 *   - `tpls` is an array of fully built TplNode trees (multiple when root is a fragment).
 *   - `finalize(opts)` must be called inside `studioCtx.change()` to apply
 *     animation sequences, variant styles, and image assets to the Site.
 */
export async function htmlToTpl(
  html: string,
  opts: {
    site: Site;
    vtm: VariantTplMgr;
    appCtx: AppCtx;
  }
): Promise<HtmlToTplResult | null> {
  const { site, vtm, appCtx } = opts;

  const { wiTree, animationSequences } = await parseHtmlToWebImporterTree(
    html,
    site
  );

  if (!wiTree) {
    return null;
  }

  const result = await wiTreeToTpl(wiTree, { site, vtm, appCtx });
  if (!result) {
    return null;
  }

  const { tpls, tplImageAssetMap, tplVariantSettingsData } = result;

  return {
    tpls,
    finalize: (finalizeOpts) => {
      // Process Animation Sequences (keyframes)
      upsertAnimationSequences(animationSequences, { site });

      const owningComponent = finalizeOpts.component;

      // Process all variant settings data to apply styles
      for (const [tplNode, vsData] of tplVariantSettingsData.entries()) {
        for (const vs of vsData) {
          const { variantCombo, safeStyles, unsafeStyles, wiAnimations } = vs;

          const animations = wiAnimations
            ? wiAnimationsToSiteAnimations(wiAnimations, { site })
            : null;

          // Process style variants by creating private style variants
          const processedVariantCombo = withoutNils(
            variantCombo.map((wiVariant) => {
              switch (wiVariant.type) {
                case "base": {
                  return getBaseVariant(owningComponent);
                }
                case VariantGroupType.GlobalScreen: {
                  return findMatchingScreenVariant(site, wiVariant);
                }
                case "style": {
                  // Currently, in HTML parser, we support for private style variants that only works on TplTag i.e
                  // Element states doesn't apply on TplComponent and TplSlot.
                  if (!isKnownTplTag(tplNode)) {
                    return null;
                  }

                  const selectors = wiVariant.selectors.map((s) => `:${s}`);
                  const existingPrivateStyleVariant =
                    getPrivateStyleVariantsForTag(
                      owningComponent,
                      tplNode,
                      selectors
                    )[0];

                  return (
                    existingPrivateStyleVariant ||
                    finalizeOpts.tplMgr.createPrivateStyleVariant(
                      owningComponent,
                      tplNode,
                      selectors
                    )
                  );
                }
              }
            })
          );

          applyVariantStyles(
            vtm,
            tplNode,
            processedVariantCombo,
            safeStyles,
            unsafeStyles,
            animations,
            finalizeOpts.ccRegistry
          );
        }
      }

      // if we have any image/svg tpls we need to create their respective assets and update their attrs accordingly
      for (const [assetTpl, assetData] of tplImageAssetMap) {
        const { asset } = finalizeOpts.tplMgr.getOrCreateImageAsset(
          assetData.image,
          assetData.options
        );

        const vs = ensureVariantSetting(assetTpl, []);
        const assetAttrs = L.assign({
          [getTagAttrForImageAsset(asset.type as ImageAssetType)]:
            new ImageAssetRef({ asset }),
        });
        L.merge(vs.attrs, assetAttrs);
      }
    },
  };
}

// Attrs handled via dedicated paths instead of vs.attrs. Exported so copilot
// tools can reuse the same exclusion list.
export const htmlAttrsIgnoredByTpl = new Set([
  "class", // RuleSet styles
  "className", // RuleSet styles
  "style", // RuleSet styles (split to safe/unsafe)
  "data-plasmic-name", // Tpl name
  "data-plasmic-component", // Plasmic metadata
  "data-plasmic-project", // Plasmic metadata (imported-project disambiguation)
  "data-props", // Plasmic metadata
  "slot", // Plasmic slot
  "src", // image asset
  "srcset", // image asset
]);

/** Matches both lowercase HTML (`onclick`) and camelCase React (`onClick`). */
export function isHtmlEventHandlerAttr(name: string) {
  return /^on[a-zA-Z]/.test(name);
}

/**
 * Camelcase the first letter of an HTML event-handler attribute, e.g. `onclick` -> `onClick`.
 * Compound names like `onmousedown` become `onMousedown` (not `onMouseDown`); codegen
 * only requires `startsWith("on")` so they still get picked up as handlers.
 */
export function toReactEventAttr(name: string) {
  if (/^on[A-Z]/.test(name)) {
    return name;
  }
  return "on" + name.charAt(2).toUpperCase() + name.slice(3);
}

/**
 * Wrap a JS string from an HTML event-handler attribute into an `EventHandler` expr
 * containing a single customFunction interaction.
 */
export function mkEventHandlerExprFromHtmlAttrValue(
  jsCode: string
): EventHandler {
  const eventHandler = new EventHandler({ interactions: [] });
  const interaction = new Interaction({
    interactionName: "Run code",
    actionName: "customFunction",
    condExpr: null,
    conditionalMode: InteractionConditionalMode.Always,
    args: [
      mkNameArg({
        name: "customFunction",
        expr: new FunctionExpr({
          bodyExpr: customCode(jsCode),
          argNames: [],
        }),
      }),
    ],
    parent: eventHandler,
    uuid: mkShortId(),
  });
  eventHandler.interactions.push(interaction);
  return eventHandler;
}

type TplVariantSettingsData = {
  variantCombo: WIVariant[];
  safeStyles: Record<string, string>;
  unsafeStyles: Record<string, string>;
  wiAnimations: CssAnimation[] | null;
};

function applyVariantStyles(
  vtm: VariantTplMgr,
  tpl: TplNode,
  variantCombo: VariantCombo,
  safeStyles: Record<string, string>,
  unsafeStyles: Record<string, string>,
  animations: Animation[] | null,
  ccRegistry: CodeComponentsRegistry
) {
  const vs = vtm.ensureVariantSetting(tpl, variantCombo);
  // Only styles Studio allows on this tpl may enter the RuleSet; the rest
  // are silently dropped since the paste flow has no error channel at the moment.
  const { valid } = validateStylesForTpl(safeStyles, tpl, vs, ccRegistry);
  RSH(vs.rs, tpl).merge(valid);

  if (Object.keys(unsafeStyles).length > 0) {
    vs.attrs["style"] = code(JSON.stringify(unsafeStyles));
  }

  vs.rs.animations = animations;
}

function findMatchingScreenVariant(
  site: Site,
  screenVariantInCombo: WIScreenVariant
) {
  const activeScreenGroup = site.activeScreenVariantGroup;
  const orderedScreenVariants = activeScreenGroup
    ? getOrderedScreenVariantSpecs(site, activeScreenGroup)
    : [];

  for (const orderScreenVariant of orderedScreenVariants) {
    const { variant: screenVariant, screenSpec } = orderScreenVariant;
    if (!screenVariant.mediaQuery) {
      continue;
    }

    const strategy = getResponsiveStrategy(site);
    const screenWidthToMatch =
      strategy === ResponsiveStrategy.mobileFirst
        ? screenSpec.minWidth
        : screenSpec.maxWidth;

    if (
      screenWidthToMatch &&
      screenWidthToMatch === screenVariantInCombo.width
    ) {
      return screenVariant;
    }
  }
  return null;
}

async function wiTreeToTpl(
  wiTree: WIElement,
  opts: { site: Site; vtm: VariantTplMgr; appCtx: AppCtx }
) {
  const { site, vtm, appCtx } = opts;
  const tplImageAssetMap = new Map<
    TplTag,
    {
      image: ResizableImage;
      options: ImageAssetOpts;
    }
  >();
  const tplVariantSettingsData = new Map<TplNode, TplVariantSettingsData[]>();

  function collectWIVariantData(
    node: Exclude<WIElement, WIFragment>,
    tpl: TplNode
  ) {
    const defaultStyles: Record<string, string> =
      node.type === "text"
        ? {}
        : {
            display: "flex",
            flexDirection: "column",
          };

    if (node.type === "svg") {
      defaultStyles["width"] = node.width;
      defaultStyles["height"] = node.height;
    }

    // Find base variant settings
    const baseVariantSetting = node.variantSettings.find(
      isWIBaseVariantSettings
    );

    const baseStyles = {
      ...defaultStyles,
      ...(baseVariantSetting?.safeStyles || {}),
    };
    const {
      animationStyles: baseAnimationStyles,
      remainingStyles: safeBaseStyles,
    } = splitStylesByAnimations(baseStyles);
    const baseAnimations = parseCssAnimationsFromStyles(baseAnimationStyles);

    const unsafeBaseStyles = {
      ...(baseVariantSetting?.unsafeStyles || {}),
    };

    // Initialize variant settings data for this TPL
    const tplVariantSettings: TplVariantSettingsData[] = [];
    // Add base variant
    tplVariantSettings.push({
      variantCombo: [{ type: "base" }],
      safeStyles: safeBaseStyles,
      unsafeStyles: unsafeBaseStyles,
      wiAnimations: baseAnimations,
    });

    // Process non-base variants
    for (const variantSetting of node.variantSettings) {
      // Skip base variant (already processed)
      if (isWIBaseVariantSettings(variantSetting)) {
        continue;
      }

      const { animationStyles, remainingStyles: vsSafeStyles } =
        splitStylesByAnimations(variantSetting.safeStyles);
      const animations = parseCssAnimationsFromStyles(animationStyles);

      const tplVSData: TplVariantSettingsData = {
        variantCombo: [],
        safeStyles: vsSafeStyles,
        unsafeStyles: { ...unsafeBaseStyles, ...variantSetting.unsafeStyles },
        wiAnimations: animations,
      };

      // Find screen and style variants in combo
      const screenVariantInCombo = variantSetting.variantCombo.find(
        (v) => v.type === VariantGroupType.GlobalScreen
      ) as WIScreenVariant | undefined;

      if (screenVariantInCombo) {
        const matchingScreenVariant = findMatchingScreenVariant(
          site,
          screenVariantInCombo
        );
        if (matchingScreenVariant) {
          tplVSData.variantCombo.push(screenVariantInCombo);
        }
      }

      const styleVariantInCombo = variantSetting.variantCombo.find(
        (v) => v.type === "style"
      ) as WIStyleVariant | undefined;

      if (styleVariantInCombo) {
        tplVSData.variantCombo.push(styleVariantInCombo);
      }

      // Make sure we have at-least one valid variant.
      if (tplVSData.variantCombo.length > 0) {
        tplVariantSettings.push(tplVSData);
      }
    }

    tplVariantSettingsData.set(tpl, tplVariantSettings);
  }

  function htmlAttrsToTplAttrs(node: WIBase): AttrsSpec {
    const result: AttrsSpec = {};
    for (const [key, value] of Object.entries(node.attrs)) {
      if (htmlAttrsIgnoredByTpl.has(key)) {
        continue;
      }
      if (isHtmlEventHandlerAttr(key)) {
        if (!value.trim()) {
          continue;
        }
        result[toReactEventAttr(key)] =
          mkEventHandlerExprFromHtmlAttrValue(value);
        continue;
      }
      result[key] = value;
    }
    return result;
  }

  async function rec(node: WIElement): Promise<TplNode[]> {
    // Fragment expands its children in place
    if (node.type === "fragment") {
      return (
        await Promise.all(node.children.map((child) => rec(child)))
      ).flat();
    }

    const tplName = node.attrs["data-plasmic-name"];
    if (node.type === "text") {
      const tpl = vtm.mkTplTagX(node.tag, {
        attrs: htmlAttrsToTplAttrs(node),
        name: tplName,
        type: TplTagType.Text,
      });
      const vs = vtm.ensureBaseVariantSetting(tpl);
      vs.text = new RawText({
        markers: [],
        text: node.text,
      });
      collectWIVariantData(node, tpl);
      return [tpl];
    }

    if (node.type === "svg") {
      const svgImage = await readAndSanitizeSvgXmlAsImage(
        appCtx,
        node.outerHtml
      );

      if (svgImage) {
        const { imageResult, opts: imageOpts } = await maybeUploadImage(
          appCtx,
          svgImage,
          undefined,
          undefined
        );
        if (!imageResult || !imageOpts) {
          return [];
        }

        const tpl = vtm.mkTplImage({
          type: imageOpts.type,
          iconColor: imageOpts.iconColor,
          name: tplName,
        });
        collectWIVariantData(node, tpl);

        // We will store each image to it's corresponding tpl so we can process it
        // later to upload image and attach asset to this tpl in 'processWebImporterTree',
        // We cannot do that here because this function is expected to be called outside 'studioCtx.change' and
        // creating an asset here would cause a model change to occur.
        tplImageAssetMap.set(tpl, {
          image: imageResult,
          options: imageOpts,
        });

        return [tpl];
      }
      return [];
    }

    if (node.type === "component") {
      const componentName = node.component;
      const component = deserializePlasmicComponentAttrs(site, {
        "data-plasmic-component": componentName,
        "data-plasmic-project": node.depProjectId as ProjectId | undefined,
      });
      if (!component) {
        throw new Error(
          node.depProjectId
            ? `Component "${componentName}" not found in imported project "${node.depProjectId}"`
            : `Component not found with name ${componentName}`
        );
      }

      // Build args from props and slots
      const args: Record<string, any> = {};

      if (node.props) {
        for (const [propName, propValue] of Object.entries(node.props)) {
          const [param, argValue] = getComponentArgFromHtmlProp(
            component,
            componentName,
            propName,
            propValue
          );
          args[param.variable.name] = argValue;
        }
      }

      if (node.slots) {
        for (const [slotName, slotChildren] of Object.entries(node.slots)) {
          const param = component.params.find(
            (p) => paramToVarName(component, p) === toVarName(slotName)
          );
          if (!param) {
            throw new Error(
              `Slot ${slotName} doesn't exist in component ${componentName}`
            );
          }

          // Recursively convert slot children to TplNodes
          args[param.variable.name] = (
            await Promise.all(slotChildren.map((child) => rec(child)))
          ).flat();
        }
      }

      const tplComponent = vtm.mkTplComponentX({
        component,
        name: tplName,
        args,
      });
      collectWIVariantData(node, tplComponent);
      return [tplComponent];
    }

    if (node.tag === "img") {
      const getSrc = () => {
        if (node.attrs.srcset) {
          const options = node.attrs.srcset.split("\n");
          const src = options[options.length - 1].split(" ")[0];
          return src;
        }
        return node.attrs.src;
      };

      const tpl = vtm.mkTplImage({
        attrs: {
          ...htmlAttrsToTplAttrs(node),
          src: code(JSON.stringify(getSrc())),
        },
        type: ImageAssetType.Picture,
        name: tplName,
      });
      collectWIVariantData(node, tpl);
      return [tpl];
    }

    if (node.type === "container") {
      const tpl = vtm.mkTplTagX(
        node.tag,
        {
          attrs: htmlAttrsToTplAttrs(node),
          name: tplName,
          type: TplTagType.Other,
        },
        (
          await Promise.all(
            node.children.map(async (child) => await rec(child))
          )
        ).flat()
      );

      collectWIVariantData(node, tpl);

      return [tpl];
    }

    assertNever(node);
  }

  const tpls = await rec(wiTree);

  if (tpls.length === 0) {
    return null;
  }

  return {
    tpls,
    tplImageAssetMap,
    tplVariantSettingsData,
  };
}

/**
 * Upsert WIAnimationSequences into the site. If a sequence with the same
 * name already exists, its keyframes are replaced
 * with the new ones. Otherwise a new AnimationSequence is created.
 */
export function upsertAnimationSequences(
  animationSequences: WIAnimationSequence[],
  opts: { site: Site }
): AnimationSequence[] {
  const { site } = opts;
  const result: AnimationSequence[] = [];

  for (const sequence of animationSequences) {
    const keyframes = sequence.keyframes.map(
      (wiKeyframe) =>
        new KeyFrame({
          percentage: wiKeyframe.percentage,
          // We will only utilize the safe styles here. We need to think about the unsafe styles since we don't have any
          // better way to display them in MixinControls/AnimationSequenceControls. We can have a new custom style attribute section
          // to store unsafe styles or arbitrary css. Since it doesn't exist yet.
          rs: mkRuleSet({
            values: camelCssPropsToKebab(wiKeyframe.safeStyles),
          }),
        })
    );

    const sequenceVarName = toVarName(sequence.name);
    const existingSequence = site.animationSequences.find(
      (existing) => toVarName(existing.name) === sequenceVarName
    );

    if (existingSequence) {
      // Replace the keyframes on the existing sequence so any references
      // to it (Animation.sequence on tpl RuleSets) keep pointing to the
      // same AnimationSequence object.
      existingSequence.keyframes = keyframes;
      result.push(existingSequence);
    } else {
      const newSequence = new AnimationSequence({
        name: sequence.name,
        uuid: mkShortId(),
        keyframes,
      });
      site.animationSequences.push(newSequence);
      result.push(newSequence);
    }
  }

  return result;
}

/**
 * Resolve a list of CssAnimation entries (from parsed `animation`
 * shorthand).
 * CssAnimations whose name doesn't match any existing
 * AnimationSequence are silently dropped.
 */
export function wiAnimationsToSiteAnimations(
  wiAnimations: CssAnimation[],
  opts: { site: Site }
) {
  const { site } = opts;
  const animations: Animation[] = [];

  for (const wiAnim of wiAnimations) {
    const animSeqUuid = tryGetAnimationSequenceUuidFromCssVar(wiAnim.name);
    const animationSequences = allAnimationSequences(site, {
      includeDeps: "direct",
    });
    const animationSequence = animationSequences.find(
      (seq) =>
        seq.uuid === animSeqUuid ||
        toVarName(seq.name) === toVarName(wiAnim.name)
    );

    if (!animationSequence) {
      continue;
    }

    animations.push(
      new Animation({
        sequence: animationSequence,
        timingFunction: wiAnim.timingFunction,
        duration: wiAnim.duration,
        delay: wiAnim.delay,
        iterationCount: wiAnim.iterationCount,
        direction: wiAnim.direction,
        fillMode: wiAnim.fillMode,
        playState: wiAnim.playState,
      })
    );
  }
  return animations;
}

function splitStylesByAnimations(styles: Record<string, string>): {
  animationStyles: Record<AnimationProperty, string>;
  remainingStyles: Record<string, string>;
} {
  const remainingStyles: Record<string, string> = {};
  const animationStyles: Record<string, string> = {};

  // Separate animation properties from other styles
  for (const [key, value] of Object.entries(styles)) {
    if (isAnimationProperty(key)) {
      animationStyles[key] = value;
    } else {
      remainingStyles[key] = value;
    }
  }

  return { animationStyles, remainingStyles };
}

/**
 * Converts an HTML prop name and value (in the serialized data-props format)
 * to the matching component Param and arg Expr.
 *
 * Throws on invalid prop name, slot params, or type mismatches.
 */
export function getComponentArgFromHtmlProp(
  component: Component,
  componentName: string,
  propName: string,
  value: unknown
): [Param, VariantsRef | CustomCode] {
  const name = toVarName(propName);
  const param = component.params.find(
    (p) => paramToVarName(component, p) === name
  );

  if (!param) {
    throw new Error(`Component "${componentName}" has no prop "${propName}"`);
  }

  if (isSlot(param)) {
    throw new Error(
      `Component "${componentName}" prop "${propName}" is a slot — pass slot content as children, not as a data-prop attribute`
    );
  }

  if (value === undefined) {
    throw new Error(
      `Component "${componentName}" prop "${propName}" has undefined value`
    );
  }

  // Variant group handling
  const variantGroup = component.variantGroups.find(
    (group) => group.param === param
  );
  if (variantGroup) {
    if (isStandaloneVariantGroup(variantGroup)) {
      if (value !== true) {
        throw new Error(
          `Component "${componentName}" prop "${propName}" is a standalone variant toggle and expects true, got ${JSON.stringify(
            value
          )}`
        );
      }
      return [param, new VariantsRef({ variants: [variantGroup.variants[0]] })];
    } else if (variantGroup.multi) {
      const values = isArray(value) ? value : [value];
      const variants = values.map((v) => {
        const variant = variantGroup.variants.find(
          (vv) => toVarName(vv.name) === toVarName(`${v}`)
        );
        if (!variant) {
          throw new Error(
            `Component "${componentName}" prop "${propName}" has no variant matching "${v}"`
          );
        }
        return variant;
      });
      return [param, new VariantsRef({ variants })];
    } else {
      const variant = variantGroup.variants.find(
        (v) => toVarName(v.name) === toVarName(`${value}`)
      );
      if (!variant) {
        throw new Error(
          `Component "${componentName}" prop "${propName}" has no variant matching "${value}"`
        );
      }
      return [param, new VariantsRef({ variants: [variant] })];
    }
  }

  // Primitive-valued types (bool, num, text/img/href/target).
  const tsType = wabToTsType(param.type);
  if (tsType === "boolean" || tsType === "number" || tsType === "string") {
    if (typeof value !== tsType) {
      throw new Error(
        `Component "${componentName}" prop "${propName}" expects a ${tsType} but got ${JSON.stringify(
          value
        )}`
      );
    }
    return [param, code(JSON.stringify(value))];
  }

  if (isChoiceType(param.type)) {
    const options = param.type.options.map((opt) =>
      typeof opt === "object" ? opt.value : opt
    );
    if (!options.some((opt) => opt === value)) {
      throw new Error(
        `Component "${componentName}" prop "${propName}" must be one of ${JSON.stringify(
          options
        )} but got ${JSON.stringify(value)}`
      );
    }
    return [param, code(JSON.stringify(value))];
  }

  if (isMultiChoiceType(param.type)) {
    const options = param.type.options.map((opt) =>
      typeof opt === "object" ? opt.value : opt
    );
    if (!Array.isArray(value)) {
      throw new Error(
        `Component "${componentName}" prop "${propName}" expects an array but got ${JSON.stringify(
          value
        )}`
      );
    }
    const invalidValues = value.filter(
      (v) => !options.some((opt) => opt === v)
    );
    if (invalidValues.length > 0) {
      throw new Error(
        `Component "${componentName}" prop "${propName}" values must be from ${JSON.stringify(
          options
        )} but got invalid values: ${JSON.stringify(invalidValues)}`
      );
    }
    return [param, code(JSON.stringify(value))];
  }

  // dateString carries a single ISO date string.
  if (isKnownDateString(param.type)) {
    if (typeof value !== "string") {
      throw new Error(
        `Component "${componentName}" prop "${propName}" expects a date string but got ${JSON.stringify(
          value
        )}`
      );
    }
    return [param, code(JSON.stringify(value))];
  }

  // dateRangeStrings carries a [from, to] pair of ISO date strings; either
  // end may be null for an open range.
  if (isKnownDateRangeStrings(param.type)) {
    if (
      !isArray(value) ||
      value.length != 2 ||
      value.some((v) => typeof v !== "string" && v !== null)
    ) {
      throw new Error(
        `Component "${componentName}" prop "${propName}" expects an array of [from, to] date strings but got ${JSON.stringify(
          value
        )}`
      );
    }
    return [param, codeLit(value as JsonValue)];
  }

  // Untyped ('any') props accept arbitrary JSON (objects, arrays, null, and
  // scalars). Stored as an unparenthesized code literal (codeLit), the same
  // form the studio prop editor stores, so tryExtractJson can read it back
  // when serializing the instance.
  if (isAnyType(param.type)) {
    return [param, codeLit(value as JsonValue)];
  }

  throw new Error(
    `Component "${componentName}" prop "${propName}" of type "${param.type.name}" is not supported yet.`
  );
}
